/* eslint-disable complexity */

'use client'

import React, { useState } from 'react'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined'
import BloodtypeOutlinedIcon from '@mui/icons-material/BloodtypeOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import StarOutlinedIcon from '@mui/icons-material/StarOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import { Box, Collapse, Typography } from '@mui/material'
import {
  Accordion,
  BreadcrumbTrail,
  FinalPriceCard,
  HtmlPurifier,
} from '@components/common'
import { GenericTestSections } from '@components/feature/FindSingleTest'
import { capitalizeWords } from '@utils/common'
import { getCityTestMetadata, getPackageMetadata } from '@utils/seo-metadata'
import { getStyles } from '@utils/styles'
import { AdvancedDiabetesAssessmentSectionData } from '../AdvancedDiabetesAssessmentSection'
import defaultStyles from './styles'

type TestDetailExperienceProps = AdvancedDiabetesAssessmentSectionData & {
  cityName: string
  genericPage?: boolean
}

export function TestDetailExperience({
  cityName,
  genericPage = false,
  faqSectionData,
  finalPriceCardData,
  testDetailsData,
  testOverviewData,
  testInclusionData,
  infoSectionData,
  sampleReportUrl,
  relatedContentData,
  itemType,
}: TestDetailExperienceProps) {
  const styles = getStyles(defaultStyles)
  const [showOverview, setShowOverview] = useState(false)
  const [openAccordion, setOpenAccordion] = useState<string | null>(null)
  const isPackage = itemType === 'Package'
  const detailLabel = isPackage ? 'Health Package' : 'Diagnostic Test'
  const title = testDetailsData?.title || detailLabel
  const formattedCity = capitalizeWords(cityName)
  let pageHeading = title
  if (!genericPage) {
    pageHeading = isPackage
      ? getPackageMetadata(title, formattedCity).h1
      : getCityTestMetadata(title, formattedCity).h1
  }
  const inclusions = testInclusionData?.testIncludes || []
  const parameterCount = inclusions.reduce(
    (count, inclusion) => count + inclusion.tests.length,
    0
  )
  let inclusionSummary = isPackage ? 'Package specific' : 'Test specific'
  if (parameterCount) inclusionSummary = `${parameterCount} included`

  const facts = [
    {
      icon: <AssignmentTurnedInOutlinedIcon />,
      label: 'Also known as',
      value: testDetailsData?.alsoKnownAs || title,
    },
    {
      icon: <BloodtypeOutlinedIcon />,
      label: 'Sample type',
      value: finalPriceCardData?.sampleType || 'As advised',
    },
    {
      icon: <PeopleAltOutlinedIcon />,
      label: 'Gender',
      value: testDetailsData?.gender || 'Male & Female',
    },
    {
      icon: <CalendarMonthOutlinedIcon />,
      label: 'Age group',
      value: testDetailsData?.ageGroup || 'All age groups',
    },
    {
      icon: <ScienceOutlinedIcon />,
      label: 'Fasting required',
      value:
        testDetailsData?.fastingRequired ||
        finalPriceCardData?.description ||
        'As advised',
    },
    {
      icon: <ScheduleOutlinedIcon />,
      label: 'Report delivery',
      value: finalPriceCardData?.delivery || 'Speak to our care team',
    },
    {
      icon: <BiotechOutlinedIcon />,
      label: isPackage ? 'Tests included' : 'Parameters',
      value: inclusionSummary,
    },
  ]
  const citySlug = cityName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')

  return (
    <Box component="main" {...styles('page')}>
      <Box component="section" {...styles('hero')}>
        <Box {...styles('container')}>
          <BreadcrumbTrail
            items={
              genericPage
                ? [{ label: 'Home', href: '/' }, { label: title }]
                : [
                    { label: 'Home', href: '/' },
                    {
                      label: isPackage
                        ? `Health Packages in ${formattedCity}`
                        : `Lab Test in ${formattedCity}`,
                      href: isPackage
                        ? `/health-check-packages/${citySlug}`
                        : `/book-a-test/${citySlug}`,
                    },
                    { label: title },
                  ]
            }
            {...styles('breadcrumb')}
          />

          <Box {...styles('heroGrid')}>
            <Box {...styles('heroContent')}>
              <Box {...styles('badges')}>
                {(testDetailsData?.isFrequentlyBooked || genericPage) && (
                  <Typography component="span" {...styles('badge')}>
                    <StarOutlinedIcon /> Frequently Booked
                  </Typography>
                )}
                {!!testDetailsData?.recentBookingCount && (
                  <Typography component="span" {...styles('locationBadge')}>
                    <span />
                    {testDetailsData.recentBookingCount.toLocaleString()} recent
                    bookings
                  </Typography>
                )}
                <Typography component="span" {...styles('locationBadge')}>
                  <span /> Available in {capitalizeWords(cityName)}
                </Typography>
              </Box>
              <Typography component="h1" {...styles('title')}>
                {pageHeading}
              </Typography>
              {(testDetailsData?.lastReviewedAt ||
                testDetailsData?.pageViewsCount ||
                testDetailsData?.verifiedBy) && (
                <Box {...styles('pageMeta')}>
                  {testDetailsData.lastReviewedAt && (
                    <Typography>
                      Last updated: {testDetailsData.lastReviewedAt}
                    </Typography>
                  )}
                  {!!testDetailsData.pageViewsCount && (
                    <Typography>
                      {testDetailsData.pageViewsCount.toLocaleString()} page
                      views
                    </Typography>
                  )}
                  {testDetailsData.verifiedBy && (
                    <Typography>
                      Verified by {testDetailsData.verifiedBy}
                    </Typography>
                  )}
                </Box>
              )}
              {testDetailsData?.itemDescription && (
                <HtmlPurifier
                  html={testDetailsData.itemDescription}
                  customStyles={{ wrapper: styles('heroDescription').sx }}
                />
              )}

              <Box {...styles('reviewCard')}>
                {testDetailsData?.reviewerImage ? (
                  <Box
                    component="img"
                    src={testDetailsData.reviewerImage}
                    alt={testDetailsData.reviewerName || 'Medical reviewer'}
                    {...styles('reviewerImage')}
                  />
                ) : (
                  <Box {...styles('reviewIcon')}>
                    <VerifiedOutlinedIcon />
                  </Box>
                )}
                <Box>
                  <Typography {...styles('reviewTitle')}>
                    {testDetailsData?.reviewerName
                      ? `Medically reviewed by ${testDetailsData.reviewerName}`
                      : 'Medically reviewed by Apollo Diagnostics'}
                  </Typography>
                  <Typography {...styles('reviewCopy')}>
                    {testDetailsData?.reviewerDesignation ||
                      'Clinical information verified by our diagnostics team'}
                  </Typography>
                </Box>
              </Box>

              <Box {...styles('factsGrid')}>
                {facts.map((fact) => (
                  <Box key={fact.label} {...styles('factCard')}>
                    <Box {...styles('factIcon')}>{fact.icon}</Box>
                    <Box>
                      <Typography {...styles('factLabel')}>
                        {fact.label}
                      </Typography>
                      <Typography {...styles('factValue')}>
                        {fact.value}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {finalPriceCardData?.description && (
                <Box {...styles('prerequisite')}>
                  <ScienceOutlinedIcon />
                  <Typography>
                    <strong>{detailLabel} prerequisites:</strong>{' '}
                    {finalPriceCardData.description}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box {...styles('priceColumn')}>
              {finalPriceCardData && (
                <FinalPriceCard
                  {...finalPriceCardData}
                  customStyles={{
                    wrapper: styles('priceCard').sx,
                    testWrapper: styles('priceCardInner').sx,
                    heading: styles('priceHeading').sx,
                    discountedPrice: styles('priceValue').sx,
                    button: styles('bookButton').sx,
                    removeButton: styles('bookButton').sx,
                    buttonWrapper: styles('mobileCartAction').sx,
                  }}
                />
              )}
              {genericPage && !finalPriceCardData && (
                <Box {...styles('genericBookingCard')}>
                  <Typography {...styles('genericBookingEyebrow')}>
                    APOLLO DIAGNOSTICS
                  </Typography>
                  <Typography component="h3" {...styles('genericBookingTitle')}>
                    {title}
                  </Typography>
                  <Typography {...styles('genericBookingCopy')}>
                    Select your city to view current price, preparation and home
                    collection availability.
                  </Typography>
                  <Box
                    component="a"
                    href={`/book-a-test/${citySlug}`}
                    {...styles('genericBookButton')}
                  >
                    Book this test
                  </Box>
                </Box>
              )}
              {sampleReportUrl && (
                <Box
                  component="a"
                  href={sampleReportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...styles('sampleReportButton')}
                >
                  View sample report
                </Box>
              )}
              <Box {...styles('assuranceList')}>
                {[
                  'Safe and hygienic sample collection',
                  'Experienced phlebotomists',
                  'Quality-assured diagnostic processing',
                  'Digital report access',
                ].map((item) => (
                  <Box key={item} {...styles('assuranceItem')}>
                    <AssignmentTurnedInOutlinedIcon />
                    <Typography>{item}</Typography>
                  </Box>
                ))}
              </Box>
              <Box
                component="a"
                href="tel:04069467075"
                {...styles('callButton')}
              >
                <PhoneOutlinedIcon /> Call to Book
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box component="section" {...styles('offersSection')}>
        <Box {...styles(['container', 'offersGrid'])}>
          {[
            {
              icon: <LocalShippingOutlinedIcon />,
              title: 'Home sample collection',
              copy: `Convenient collection available in ${capitalizeWords(cityName)}`,
            },
            {
              icon: <VerifiedOutlinedIcon />,
              title: 'Apollo quality assurance',
              copy: 'Reliable processing at accredited diagnostic laboratories',
            },
            {
              icon: <AssignmentTurnedInOutlinedIcon />,
              title: 'Digital reports',
              copy: 'Access your diagnostic reports online when ready',
            },
          ].map((offer) => (
            <Box key={offer.title} {...styles('offerCard')}>
              <Box {...styles('offerIcon')}>{offer.icon}</Box>
              <Box>
                <Typography {...styles('offerTitle')}>{offer.title}</Typography>
                <Typography {...styles('offerCopy')}>{offer.copy}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box component="section" {...styles('trustSection')}>
        <Box {...styles('container')}>
          <Box {...styles('trustHeading')}>
            <Typography component="h2" {...styles('sectionTitle')}>
              Trusted Diagnostics. Quality You Can Rely On.
            </Typography>
            <Typography {...styles('sectionSubtitle')}>
              Apollo standards across collection, processing and reporting
            </Typography>
          </Box>
          <Box {...styles('trustGrid')}>
            {[
              ['1500+', 'Diagnostic centres'],
              ['10M+', 'Tests processed annually'],
              ['NABL & CAP', 'Accredited laboratory'],
              ['Home', 'Sample collection'],
              ['Digital', 'Report access'],
            ].map(([value, label]) => (
              <Box key={label} {...styles('trustItem')}>
                <Typography {...styles('trustValue')}>{value}</Typography>
                <Typography {...styles('trustLabel')}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {genericPage && !inclusions.length && (
        <GenericTestSections
          cityName={cityName}
          phase="before-guide"
          testName={title}
        />
      )}

      <Box component="section" {...styles('contentSection')}>
        <Box {...styles('container')}>
          {!!inclusions.length && (
            <Box {...styles('contentCard')}>
              <Box {...styles('sectionHeading')}>
                <Box {...styles('sectionIcon')}>
                  <ScienceOutlinedIcon />
                </Box>
                <Box>
                  <Typography component="h2" {...styles('sectionTitle')}>
                    {testInclusionData?.title || 'Test parameters'}
                  </Typography>
                  <Typography {...styles('sectionSubtitle')}>
                    {isPackage
                      ? 'Tests and parameters included in this health package'
                      : 'Parameters included in this diagnostic test'}
                  </Typography>
                </Box>
              </Box>
              <Box {...styles('accordionList')}>
                {inclusions.map((inclusion) => (
                  <Accordion
                    label={`${inclusion.label} (${inclusion.tests.length})`}
                    key={inclusion.label}
                    isOpen={openAccordion === inclusion.label}
                    onChange={() =>
                      setOpenAccordion(
                        openAccordion === inclusion.label
                          ? null
                          : inclusion.label
                      )
                    }
                    customStyles={{
                      toggleWrapper: styles('accordionToggle').sx,
                    }}
                  >
                    <Box component="ul" {...styles('parameterList')}>
                      {inclusion.tests.map((test) => (
                        <Box component="li" key={test}>
                          {test}
                        </Box>
                      ))}
                    </Box>
                  </Accordion>
                ))}
              </Box>
            </Box>
          )}

          {testOverviewData?.testDescription && (
            <Box {...styles('guideGrid')}>
              <Box component="aside" {...styles('guideAside')}>
                <Typography {...styles('asideEyebrow')}>TEST GUIDE</Typography>
                <Typography component="h2" {...styles('asideTitle')}>
                  About {title}
                </Typography>
                <Typography {...styles('asideCopy')}>
                  Review the overview, preparation information and other useful
                  details before booking this {isPackage ? 'package' : 'test'}.
                </Typography>
              </Box>
              <Box {...styles('guideContent')}>
                <Collapse in={showOverview} collapsedSize={310}>
                  <HtmlPurifier
                    html={testOverviewData.testDescription}
                    customStyles={{ wrapper: styles('medicalContent').sx }}
                  />
                </Collapse>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowOverview((current) => !current)}
                  {...styles('readMoreButton')}
                >
                  {showOverview
                    ? 'Read less'
                    : `Read full ${isPackage ? 'package' : 'test'} guide`}
                </Box>
              </Box>
            </Box>
          )}

          {!!faqSectionData?.faqsData?.length && (
            <Box {...styles('contentCard')}>
              <Typography component="h2" {...styles('sectionTitle')}>
                Frequently asked questions
              </Typography>
              <Box {...styles('accordionList')}>
                {faqSectionData.faqsData.map(
                  (faq) =>
                    faq.document && (
                      <Accordion
                        {...faq}
                        key={faq.label}
                        isOpen={openAccordion === faq.label}
                        onChange={() =>
                          setOpenAccordion(
                            openAccordion === faq.label ? null : faq.label
                          )
                        }
                        customStyles={{
                          toggleWrapper: styles('accordionToggle').sx,
                        }}
                      />
                    )
                )}
              </Box>
            </Box>
          )}

          {infoSectionData?.description && (
            <Box {...styles('whyApollo')}>
              <VerifiedOutlinedIcon />
              <Box>
                <Typography component="h2" {...styles('sectionTitle')}>
                  {infoSectionData.title}
                </Typography>
                <Typography {...styles('whyApolloCopy')}>
                  {infoSectionData.description}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {genericPage && (
        <GenericTestSections
          cityName={cityName}
          phase="after-guide"
          testName={title}
          relatedContent={relatedContentData}
        />
      )}
    </Box>
  )
}
